import React, { useEffect, useState, useCallback } from 'react';
import './UserProfile.css';
import '@/component/project/MCPServerSetting/MCPServerSetting.css';
import { getMcpServerByOwnerBy, updateMCPServer } from '@/service/mcpServerService';
import LoadingPanel from '../common/LoadingPanel';
import Constant from '@/common/constant/Constant';
import APIAmplify from '@aws-amplify/api';
import Utils from '@/common/utils/utils';
import { onPersonalConnectorServiceUpdated } from '@/graphql/subscriptions';
import { I18n } from 'aws-amplify';

const INITIAL_PERSONAL_CONNECTOR_SERVICES = {
    [Constant.PERSONAL_CONNECTOR_SERVICE_NAME.DROPBOX]: {
        id: Constant.PERSONAL_CONNECTOR_COMMON_ID.DROPBOX,
        serviceName: Constant.PERSONAL_CONNECTOR_SERVICE_NAME.DROPBOX,
        isConnected: false,
        oauth2Setting: {}
    },
    [Constant.PERSONAL_CONNECTOR_SERVICE_NAME.GMAIL]: {
        id: Constant.PERSONAL_CONNECTOR_COMMON_ID.GMAIL,
        serviceName: Constant.PERSONAL_CONNECTOR_SERVICE_NAME.GMAIL,
        isConnected: false,
        oauth2Setting: {}
    },
    [Constant.PERSONAL_CONNECTOR_SERVICE_NAME.GOOGLE_CALENDAR]: {
        id: Constant.PERSONAL_CONNECTOR_COMMON_ID.GOOGLE_CALENDAR,
        serviceName: Constant.PERSONAL_CONNECTOR_SERVICE_NAME.GOOGLE_CALENDAR,
        isConnected: false,
        oauth2Setting: {}
    },
    [Constant.PERSONAL_CONNECTOR_SERVICE_NAME.GOOGLE_DRIVE]: {
        id: Constant.PERSONAL_CONNECTOR_COMMON_ID.GOOGLE_DRIVE,
        serviceName: Constant.PERSONAL_CONNECTOR_SERVICE_NAME.GOOGLE_DRIVE,
        isConnected: false,
        oauth2Setting: {}
    },
    [Constant.PERSONAL_CONNECTOR_SERVICE_NAME.MICROSOFT_TEAM]: {
        id: Constant.PERSONAL_CONNECTOR_COMMON_ID.MICROSOFT_TEAM,
        serviceName: Constant.PERSONAL_CONNECTOR_SERVICE_NAME.MICROSOFT_TEAM,
        isConnected: false,
        oauth2Setting: {}
    },
    [Constant.PERSONAL_CONNECTOR_SERVICE_NAME.OUTLOOK_GMAIL]: {
        id: Constant.PERSONAL_CONNECTOR_COMMON_ID.OUTLOOK_GMAIL,
        serviceName: Constant.PERSONAL_CONNECTOR_SERVICE_NAME.OUTLOOK_GMAIL,
        isConnected: false,
        oauth2Setting: {}
    },
    [Constant.PERSONAL_CONNECTOR_SERVICE_NAME.OUTLOOK_CALENDAR]: {
        id: Constant.PERSONAL_CONNECTOR_COMMON_ID.OUTLOOK_CALENDAR,
        serviceName: Constant.PERSONAL_CONNECTOR_SERVICE_NAME.OUTLOOK_CALENDAR,
        isConnected: false,
        oauth2Setting: {}
    },
    [Constant.PERSONAL_CONNECTOR_SERVICE_NAME.SHAREPOINT]: {
        id: Constant.PERSONAL_CONNECTOR_COMMON_ID.SHAREPOINT,
        serviceName: Constant.PERSONAL_CONNECTOR_SERVICE_NAME.SHAREPOINT,
        isConnected: false,
        oauth2Setting: {}
    }
};

export default function ExternalIntegrationTab({ userDetail, API }) {
    const [externalServices, setExternalServices] = useState([]);
    const [isLoading, setIsLoading] = useState(false);

    const sortServicesAlphabetically = (services) => {
        return [...services].sort((a, b) => {
            const nameA = (a.serviceName || '').toLowerCase();
            const nameB = (b.serviceName || '').toLowerCase();
            return nameA.localeCompare(nameB);
        });
    };

    /**
     * Update state on ANY status change (not just when changing to SUCCESS).
     * Only updates externalServices if the status value (isConnected) differs from current state.
     */
    const handlePersonalConnectorServiceUpdated = useCallback(subscriptionData => {
        const updatedConnector = subscriptionData?.value?.data?.onPersonalConnectorServiceUpdated;
        if (!updatedConnector?.serverName) return;

        const updatedServiceName = updatedConnector.serverName;
        const updatedStatus = updatedConnector.status;
        const updatedIsConnected = updatedStatus === Constant.MCP_SERVER_CONNECT_STATUS.SUCCESS;

        setExternalServices(prevServices => {
            const idx = prevServices.findIndex(s => s.serviceName === updatedServiceName);
            if (idx === -1) return prevServices;

            const currentService = prevServices[idx];
            if (currentService.isConnected === updatedIsConnected) {
                if (currentService.id === updatedConnector.id) {
                    return prevServices;
                }
                const nextServices = [...prevServices];
                nextServices[idx] = {
                    ...currentService,
                    id: updatedConnector.id
                };
                return sortServicesAlphabetically(nextServices);
            }

            const nextServices = [...prevServices];
            nextServices[idx] = {
                ...currentService,
                id: updatedConnector.id,
                isConnected: updatedIsConnected
            };
            return sortServicesAlphabetically(nextServices);
        });
    }, []);

    useEffect(() => {
        setIsLoading(true);
        getMcpServerByOwnerBy(userDetail.affiliationOrganization, userDetail.id)
            .then(mcpList => {
                let listPersonalConnectorService = mcpList?.map(mcp => {
                    return {
                        id: mcp.id,
                        serviceName: mcp.serverName,
                        isConnected: mcp.status === Constant.MCP_SERVER_CONNECT_STATUS.SUCCESS,
                        oauth2Setting: mcp.oauth2Setting
                    };
                });


                let listPersonalConnectorServiceName = listPersonalConnectorService.map(
                    mcp => mcp.serviceName
                );
                let listConstantPersonalConnectorService = Object.values(
                    Constant.PERSONAL_CONNECTOR_SERVICE_NAME
                );

                for (const serviceName of listConstantPersonalConnectorService) {
                    if (!listPersonalConnectorServiceName.includes(serviceName)) {
                        listPersonalConnectorService.push(
                            INITIAL_PERSONAL_CONNECTOR_SERVICES[serviceName]
                        );
                    }
                }

                const sortedServices = sortServicesAlphabetically(listPersonalConnectorService);
                setExternalServices(sortedServices);
            })
            .finally(() => {
                setIsLoading(false);
            });

        const subscription = APIAmplify.graphql({
            query: onPersonalConnectorServiceUpdated,
            variables: { ownerBy: userDetail.id },
            authMode: Constant.AUTH_MODE_API_KEY
        }).subscribe({
            next: data => handlePersonalConnectorServiceUpdated(data),
            error: error => {
                console.error('Subscription error:', error);
            }
        });
        return () => subscription?.unsubscribe();
    }, [userDetail]);

    const handleToggleConnection = connector => {
        if (connector.isConnected) {
            disconnectPersonalConnector(connector);
        } else {

            authByOAuth2(connector);
        }
    };

    const disconnectPersonalConnector = async (personalConnector) => {
        setIsLoading(true);
        try {
            const requestDto = {
                corporationInuse: userDetail.affiliationOrganization,
                projectInuse: null,
                userId: userDetail.id,
                handleMethod: Constant.MCP_SERVER_UPDATE_ACTION_TYPE.DISCONNECT_PERSONAL_CONNECTOR,
                authData: {
                    id: personalConnector.id,
                    serviceName: personalConnector.serviceName
                }
            };
            
            const result = await updateMCPServer(requestDto);
            
            if (result?.success) {
                setExternalServices(prevServices => {
                    const idx = prevServices.findIndex(s => s.serviceName === personalConnector.serviceName);
                    if (idx === -1) return prevServices;
                    
                    const nextServices = [...prevServices];
                    nextServices[idx] = {
                        ...nextServices[idx],
                        isConnected: false
                    };
                    return sortServicesAlphabetically(nextServices);
                });
            }
        } catch (error) {
            console.error('disconnectPersonalConnector error:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const authByOAuth2 = (personalConnector) => {
        const oauth2Setting = personalConnector.oauth2Setting;
        let authUrl = '';
        let clientId = '';
        let redirectUri = `${process.env.REACT_APP_DOMAIN_NAME}oauth2callback`;
        let scope = '';
        if(!Utils.isEmptyObject(oauth2Setting)) {
             authUrl = oauth2Setting.authUrl;
             clientId = oauth2Setting.clientId;
            redirectUri = `${process.env.REACT_APP_DOMAIN_NAME}oauth2callback`
            scope = oauth2Setting.scope;
        }else{
            switch(personalConnector.serviceName){
                case Constant.PERSONAL_CONNECTOR_SERVICE_NAME.DROPBOX:
                    authUrl = Constant.PERSONAL_CONNECTOR_OAUTH2_URL.DROPBOX;
                    clientId = process.env.REACT_APP_DROPBOX_CLIENT_ID;
                    scope = Constant.PERSONAL_CONNECTOR_OAUTH2_SCOPE.DROPBOX;
                    break;
                case Constant.PERSONAL_CONNECTOR_SERVICE_NAME.GMAIL:
                    authUrl = Constant.PERSONAL_CONNECTOR_OAUTH2_URL.GOOGLE;
                    clientId = process.env.REACT_APP_GMAIL_CLIENT_ID;
                    scope = Constant.PERSONAL_CONNECTOR_OAUTH2_SCOPE.GMAIL;
                    break;
                case Constant.PERSONAL_CONNECTOR_SERVICE_NAME.GOOGLE_CALENDAR:
                    authUrl = Constant.PERSONAL_CONNECTOR_OAUTH2_URL.GOOGLE;
                    clientId = process.env.REACT_APP_GMAIL_CLIENT_ID;
                    scope = Constant.PERSONAL_CONNECTOR_OAUTH2_SCOPE.GOOGLE_CALENDAR;
                    break;
                case Constant.PERSONAL_CONNECTOR_SERVICE_NAME.GOOGLE_DRIVE:
                    authUrl = Constant.PERSONAL_CONNECTOR_OAUTH2_URL.GOOGLE;
                    clientId = process.env.REACT_APP_GMAIL_CLIENT_ID;
                    scope = Constant.PERSONAL_CONNECTOR_OAUTH2_SCOPE.GOOGLE_DRIVE;
                    break;
                case Constant.PERSONAL_CONNECTOR_SERVICE_NAME.MICROSOFT_TEAM:
                    authUrl = Constant.PERSONAL_CONNECTOR_OAUTH2_URL.MICROSOFT;
                    clientId = process.env.REACT_APP_MICROSOFT_CLIENT_ID;
                    scope = Constant.PERSONAL_CONNECTOR_OAUTH2_SCOPE.MICROSOFT_TEAM;
                    break;
                case Constant.PERSONAL_CONNECTOR_SERVICE_NAME.OUTLOOK_GMAIL:
                    authUrl = Constant.PERSONAL_CONNECTOR_OAUTH2_URL.MICROSOFT;
                    clientId = process.env.REACT_APP_MICROSOFT_CLIENT_ID;
                    scope = Constant.PERSONAL_CONNECTOR_OAUTH2_SCOPE.OUTLOOK_GMAIL;
                    break;
                case Constant.PERSONAL_CONNECTOR_SERVICE_NAME.OUTLOOK_CALENDAR:
                    authUrl = Constant.PERSONAL_CONNECTOR_OAUTH2_URL.MICROSOFT;
                    clientId = process.env.REACT_APP_MICROSOFT_CLIENT_ID;
                    scope = Constant.PERSONAL_CONNECTOR_OAUTH2_SCOPE.OUTLOOK_CALENDAR;
                    break;
                case Constant.PERSONAL_CONNECTOR_SERVICE_NAME.SHAREPOINT:
                    authUrl = Constant.PERSONAL_CONNECTOR_OAUTH2_URL.MICROSOFT;
                    clientId = process.env.REACT_APP_MICROSOFT_CLIENT_ID;
                    scope = Constant.PERSONAL_CONNECTOR_OAUTH2_SCOPE.SHAREPOINT;
                    break;
                default:
                    break;
            }
        }
      
        if (Array.isArray(scope)) {
            scope = scope
                .map(s => (typeof s === 'string' ? s : s?.value))
                .filter(Boolean)
                .join(' ');
        } else if (typeof scope === 'string') {
            // Normalize comma or multi-space separated strings to single-space delimited
            scope = scope
                .split(/[\s,]+/)
                .filter(Boolean)
                .join(' ');
        }

        const state = btoa(JSON.stringify({mcpId: personalConnector.id, type: Constant.MCP_SERVER_TYPE.OPENAI_CONNECTOR_PERSONAL, serviceName: personalConnector.serviceName, authUrl: authUrl, clientId: clientId, scope: scope}));
        
        // Dropbox requires token_access_type=offline, Google/Microsoft use access_type=offline
        const isDropbox = personalConnector.serviceName === Constant.PERSONAL_CONNECTOR_SERVICE_NAME.DROPBOX;
        const tokenAccessParam = isDropbox ? 'token_access_type=offline' : 'access_type=offline';
        const authUrlByOAuth2 = `${authUrl}?client_id=${clientId}&redirect_uri=${redirectUri}&response_type=code&scope=${encodeURIComponent(scope)}&${tokenAccessParam}&prompt=consent&state=${state}`;
        const popup = window.open(
            authUrlByOAuth2,
            'oauth2AuthWindow',
            'noopener,noreferrer,width=700,height=800,left=100,top=50,menubar=no,toolbar=no,location=yes,resizable=yes,scrollbars=yes,status=no'
        );
        if (popup && typeof popup.focus === 'function') {
            popup.focus();
        }
    };

    return (
        <div style={{ height: '100%' }}>
            <LoadingPanel isLoading={isLoading} />
            <div className="mcp-server-header mcp-server-line-space">
                <div className="mcp-server-header-inner">
                    <h3 className="mcp-server-setting-list">{I18n.get('MCP_SERVER_SETTING_LABEL_ORG')}</h3>
                </div>
            </div>
            <div className="mcp-server-body">
                <div className="mcp-server-list">
                    {externalServices.map(service => (
                        <div key={service.id} className="mcp-server-body-line">
                            <div className="mcp-server-item external-integration-service-name">
                                {service.serviceName}
                            </div>
                            <div
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                }}
                            >
                                <button
                                    className={
                                        service.isConnected
                                            ? 'external-integration-disconnect-btn': 'external-integration-connect-btn' 
                                    }
                                    onClick={() => handleToggleConnection(service)}
                                >
                                    {service.isConnected ? I18n.get('MCP_SERVER_SETTING_DISCONNECT') : I18n.get('MCP_SERVER_SETTING_CONNECT') }
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}
